import { create } from 'zustand';
import { api } from '../services/apiService';
import type { Approval, ApprovalStats } from '../types/approvals';

interface ApprovalFilters {
  status?: string;
  approver?: string;
  department?: string;
  submitted_by?: string;
  priority?: string;
}

interface ApprovalState {
  approvals: Approval[];
  stats: ApprovalStats | null;
  loading: boolean;
  filters: ApprovalFilters;
  fetchApprovals: () => Promise<void>;
  fetchStats: () => Promise<void>;
  createApproval: (data: Partial<Approval>) => Promise<Approval>;
  updateApproval: (id: number, data: Partial<Approval>) => Promise<Approval>;
  deleteApproval: (id: number) => Promise<void>;
  setFilters: (filters: Partial<ApprovalFilters>) => void;
}

function buildQueryString(filters: ApprovalFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.approver) params.set('approver', filters.approver);
  if (filters.department) params.set('department', filters.department);
  if (filters.submitted_by) params.set('submitted_by', filters.submitted_by);
  if (filters.priority) params.set('priority', filters.priority);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const useApprovalStore = create<ApprovalState>((set, get) => ({
  approvals: [],
  stats: null,
  loading: false,
  filters: {},

  fetchApprovals: async () => {
    try {
      set({ loading: true });
      const qs = buildQueryString(get().filters);
      const approvals = await api.get<Approval[]>(`/approvals${qs}`);
      set({ approvals, loading: false });
    } catch (err) {
      console.error('Failed to fetch approvals:', err);
      set({ loading: false });
    }
  },

  fetchStats: async () => {
    try {
      const stats = await api.get<ApprovalStats>('/approvals/stats');
      set({ stats });
    } catch (err) {
      console.error('Failed to fetch approval stats:', err);
    }
  },

  createApproval: async (data: Partial<Approval>) => {
    const approval = await api.post<Approval>('/approvals', data);
    await get().fetchApprovals();
    await get().fetchStats();
    return approval;
  },

  updateApproval: async (id: number, data: Partial<Approval>) => {
    const approval = await api.patch<Approval>(`/approvals/${id}`, data);
    await get().fetchApprovals();
    await get().fetchStats();
    return approval;
  },

  deleteApproval: async (id: number) => {
    await api.delete(`/approvals/${id}`);
    await get().fetchApprovals();
    await get().fetchStats();
  },

  setFilters: (newFilters: Partial<ApprovalFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
    get().fetchApprovals();
  },
}));
